/**
 * Main App component for activQMS
 *
 * This component sets up the main application structure with routing,
 * authentication, and global state management.
 */

import React from "react";
import { Routes, Route, Navigate, useParams } from "react-router-dom";

import { useAuthStore } from "./stores/auth-store";

import { Layout } from "./components/layout/layout";
import { LoadingSpinner } from "./components/ui/loading-spinner";
import {
  GraphQLErrorBoundary,
  ErrorBoundary,
} from "./components/error-boundary";

// Page imports
import { DashboardPage } from "./pages/dashboard/dashboard-page";
import { ConversationsPage } from "./pages/conversations/conversations-page";
import { CreateConversationPage } from "./pages/conversations/create-conversation-page";
import { ConversationDetailPage } from "./pages/conversations/conversation-detail-page";

// Redirect component for backward compatibility
function ConversationToConversationRedirect() {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/conversations/${id}`} replace />;
}

// External redirect component for activQMS website
function ExternalRedirect({ url }: { url: string }) {
  React.useEffect(() => {
    window.location.replace(url);
  }, [url]);

  return (
    <div className="min-h-screen flex items-center justify-center">
      <LoadingSpinner size="lg" />
    </div>
  );
}


import { ClientsPage } from "./pages/clients/clients-page";
import { CreateClientPage } from "./pages/clients/create-client-page";
import { ClientDetailPage } from "./pages/clients/client-detail-page";
import { EditClientPage } from "./pages/clients/edit-client-page";
import { AssetsPage } from "./pages/assets/assets-page";
import { MessageTemplatesPage } from "./pages/message-templates/message-templates-page";
import { StartMessagesPage } from "./pages/start-messages/start-messages-page";
import { AutoRespondersPage } from "./pages/auto-responders/auto-responders-page";
import { DestinationsPage } from "./pages/destinations/destinations-page";
import { AnalyticsPage } from "./pages/analytics/analytics-page";
import { UsersPage } from "./pages/users/users-page";
import { FilesPage } from "./pages/files/files-page";
import { SettingsPage } from "./pages/settings/settings-page";

// Public pages (no authentication required)
import ChatDemoPage from "./pages/chat-demo";
import PublicChatWidgetPage from "./pages/chat/public-chat-widget-page";

// Slack OAuth callback
import { SlackOAuthCallback } from "./pages/slack/oauth-callback";

// LinkedIn OAuth callback
import { LinkedInOAuthCallback } from "./pages/linkedin/oauth-callback";

// Auth page imports
import { LoginPage } from "./pages/auth/login-page";
// import { RegisterPage } from "./pages/auth/register-page";

/**
 * Main App component
 */
function AppContent(): React.ReactElement {
  // Debug environment variables in development
  React.useEffect(() => {
    if (import.meta.env.VITE_STAGE === "dev") {
      console.log("Environment Variables:", {
        VITE_USER_POOL_ID: import.meta.env.VITE_USER_POOL_ID,
        VITE_USER_POOL_CLIENT_ID: import.meta.env.VITE_USER_POOL_CLIENT_ID,
        VITE_AWS_REGION: import.meta.env.VITE_AWS_REGION,
      });
    }
  }, []);

  // Use our custom auth store
  const { isAuthenticated, isLoading, user } = useAuthStore();

  // Show loading spinner while checking authentication
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  // Handle chat routes and OAuth callback separately - no authentication or layout required
  const currentPath = window.location.pathname;
  if (
    currentPath === "/chat" ||
    currentPath === "/chat-demo" ||
    currentPath === "/slack/oauth/callback" ||
    currentPath === "/linkedin/oauth/callback"
  ) {
    return (
      <Routes>
        {/* Public chat routes */}
        <Route
          path="/chat"
          element={
            <ErrorBoundary
              fallback={
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100vh",
                    fontFamily: "system-ui, -apple-system, sans-serif",
                    textAlign: "center",
                    padding: "20px",
                  }}
                >
                  <div>
                    <h2>Chat temporarily unavailable</h2>
                    <p>Please refresh the page to try again.</p>
                  </div>
                </div>
              }
            >
              <PublicChatWidgetPage />
            </ErrorBoundary>
          }
        />
        <Route path="/chat-demo" element={<ChatDemoPage />} />
        {/* Slack OAuth Callback - no navigation */}
        <Route path="/slack/oauth/callback" element={<SlackOAuthCallback />} />
        {/* LinkedIn OAuth Callback - no navigation */}
        <Route path="/linkedin/oauth/callback" element={<LinkedInOAuthCallback />} />
      </Routes>
    );
  }

  // If authenticated, show main application
  if (isAuthenticated && user) {
    return (
      <Layout>
        <Routes>
          {/* Dashboard */}
          <Route
            path="/"
            element={<ExternalRedirect url="https://www.activqms.com" />}
          />
          <Route
            path="/dashboard"
            element={<Navigate to="/conversations" replace />}
          />
          <Route path="/dashboard" element={<DashboardPage />} />

          {/* Conversations - Central orchestration system */}
          <Route
            path="/conversations"
            element={
              <GraphQLErrorBoundary
                onAuthError={() => (window.location.href = "/auth/login")}
              >
                <ConversationsPage />
              </GraphQLErrorBoundary>
            }
          />
          {/* Redirect auth routes to conversations when authenticated */}
          <Route
            path="/auth/*"
            element={<Navigate to="/conversations" replace />}
          />
          <Route
            path="/conversations/create"
            element={
              <GraphQLErrorBoundary
                onAuthError={() => (window.location.href = "/auth/login")}
              >
                <CreateConversationPage />
              </GraphQLErrorBoundary>
            }
          />
          <Route
            path="/conversations/:id"
            element={
              <GraphQLErrorBoundary
                onAuthError={() => (window.location.href = "/auth/login")}
              >
                <ConversationDetailPage />
              </GraphQLErrorBoundary>
            }
          />

          {/* Backward compatibility redirects for old conversation URLs */}
          <Route
            path="/conversations"
            element={<Navigate to="/conversations" replace />}
          />
          <Route
            path="/conversations/create"
            element={<Navigate to="/conversations/create" replace />}
          />
          <Route
            path="/conversations/:id"
            element={<ConversationToConversationRedirect />}
          />

          {/* Client Management */}
          <Route
            path="/clients"
            element={
              <GraphQLErrorBoundary
                onAuthError={() => (window.location.href = "/auth/login")}
              >
                <ClientsPage />
              </GraphQLErrorBoundary>
            }
          />
          <Route
            path="/clients/create"
            element={
              <GraphQLErrorBoundary
                onAuthError={() => (window.location.href = "/auth/login")}
              >
                <CreateClientPage />
              </GraphQLErrorBoundary>
            }
          />
          <Route
            path="/clients/:id"
            element={
              <GraphQLErrorBoundary
                onAuthError={() => (window.location.href = "/auth/login")}
              >
                <ClientDetailPage />
              </GraphQLErrorBoundary>
            }
          />
          <Route
            path="/clients/:id/edit"
            element={
              <GraphQLErrorBoundary
                onAuthError={() => (window.location.href = "/auth/login")}
              >
                <EditClientPage />
              </GraphQLErrorBoundary>
            }
          />

          {/* Assets - Reusable file components */}
          <Route
            path="/assets"
            element={
              <GraphQLErrorBoundary
                onAuthError={() => (window.location.href = "/auth/login")}
              >
                <AssetsPage />
              </GraphQLErrorBoundary>
            }
          />

          {/* Message Templates - Pre-filled messages */}
          <Route
            path="/message-templates"
            element={
              <GraphQLErrorBoundary
                onAuthError={() => (window.location.href = "/auth/login")}
              >
                <MessageTemplatesPage />
              </GraphQLErrorBoundary>
            }
          />

          {/* Start Messages - Reusable start message components */}
          <Route
            path="/start-messages"
            element={
              <GraphQLErrorBoundary
                onAuthError={() => (window.location.href = "/auth/login")}
              >
                <StartMessagesPage />
              </GraphQLErrorBoundary>
            }
          />

          {/* Auto-Responders - Reusable message components */}
          <Route
            path="/auto-responders"
            element={
              <GraphQLErrorBoundary
                onAuthError={() => (window.location.href = "/auth/login")}
              >
                <AutoRespondersPage />
              </GraphQLErrorBoundary>
            }
          />

          {/* Destinations - Reusable URL endpoints */}
          <Route
            path="/destinations"
            element={
              <GraphQLErrorBoundary
                onAuthError={() => (window.location.href = "/auth/login")}
              >
                <DestinationsPage />
              </GraphQLErrorBoundary>
            }
          />

          {/* Analytics - Performance monitoring */}
          <Route
            path="/analytics"
            element={
              <GraphQLErrorBoundary
                onAuthError={() => (window.location.href = "/auth/login")}
              >
                <AnalyticsPage />
              </GraphQLErrorBoundary>
            }
          />

          {/* User Management */}
          <Route path="/users" element={<UsersPage />} />

          {/* File Management */}
          <Route path="/files" element={<FilesPage />} />

          {/* Settings */}
          <Route path="/settings" element={<SettingsPage />} />

          {/* 404 Redirect to homepage */}
          <Route path="*" element={<ExternalRedirect url="https://www.activqms.com" />} />
        </Routes>
      </Layout>
    );
  }

  // If not authenticated, show login page
  return (
    <Routes>
      <Route
        path="/"
        element={<ExternalRedirect url="https://www.activqms.com" />}
      />
      {/* Authentication pages */}
      <Route path="/auth/login" element={<LoginPage />} />
      {/* <Route path="/auth/register" element={<RegisterPage />} /> */}

      {/* Redirect all other routes to login */}
      <Route path="*" element={<Navigate to="/auth/login" replace />} />
    </Routes>
  );
}

export default function App(): React.ReactElement {
  return <AppContent />;
}
